import moment from 'moment'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators'

import { selectActualTimezone } from 'containers/App/selectors'

import { shareReset } from '../../../Share/actions'
import { addToast } from '../../../Toasts/actions'
import {
  approveCampaignError,
  approveCampaignSuccess,
  getCampaignError,
  getCampaignSuccess,
  removeDraftError,
  removeDraftSuccess,
  saveAndApproveCampaignError,
  saveAndApproveCampaignSuccess,
  saveAndTestCampaignError,
  saveAndTestCampaignSuccess,
  saveDraftError,
  saveDraftSuccess,
} from '../steps/actions'
import {
  GET_CAMPAIGN_ASYNC,
  INIT_EDIT_MODE,
  REMOVE_DRAFT_ASYNC,
  SAVE_AND_APPROVE_CAMPAIGN_ASYNC,
  SAVE_AND_TEST_CAMPAIGN_ASYNC,
  SAVE_DRAFT_ASYNC,
} from '../steps/constants'
import landingPageStepEpics from '../steps/LandingPageStep/epics'
import { buildManagerNotificationSavePayload } from '../steps/managerNotificationUtils'
import { sendTryItOutError, sendTryItOutSuccess } from '../steps/ReviewAndLaunchStep/actions'
import { selectTestCampaignData } from '../steps/ReviewAndLaunchStep/selectors'
import scenariosStepEpics from '../steps/ScenariosStep/epics'
import {
  selectSendCampaignFailedEmail,
  selectSendManagerReportEveryXDays,
} from '../steps/selectors'
import { validateStepFields as validateSetupStepFields } from '../steps/SetupStep/actions'
import { selectSimulationSendingRemindersCount } from '../steps/SetupStep/selectors'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../steps/TrainingModuleStep/constants'

import { MAX_TRAINING_REMINDERS } from './constants'

const mapSaveSuccessCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftSuccess,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignSuccess,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignSuccess,
}
const mapSaveErrorCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftError,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignError,
  [SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE]: saveAndTestCampaignError,
}

const getCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(INIT_EDIT_MODE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSimulationCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.wizardCampaign
        .getCampaign(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.id
        )
        .pipe(
          mergeMap((response) => of(getCampaignSuccess(response), validateSetupStepFields())),
          catchError((error) => of(epicsErrorHandler(getCampaignError, error))),
          takeUntil(action$.pipe(ofType(GET_CAMPAIGN_ASYNC.CANCEL)))
        )
    )
  )

const saveDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      SAVE_DRAFT_ASYNC.INITIATE,
      SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE,
      SAVE_AND_TEST_CAMPAIGN_ASYNC.INITIATE
    ),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSimulationCampaign) return true
      return false
    }),
    filter(() => !!state$.value.awarenessCenter?.wizardSimulationCampaign),
    mergeMap((action) => {
      const campaignData = state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign
      const trainingIds = campaignData.selectedTrainingVideo.reduce((acc, { id }) => {
        if (id !== AUTOMATED_LEVEL_BASED_TRAINING.id) {
          acc.push(id)
        }

        return acc
      }, [])
      const sendingRemindersCount = selectSimulationSendingRemindersCount(state$.value)
      const remindersCount =
        sendingRemindersCount > MAX_TRAINING_REMINDERS
          ? MAX_TRAINING_REMINDERS
          : sendingRemindersCount
      const trainingReminders =
        remindersCount && campaignData.trainingReminders.length
          ? Array(remindersCount)
              .fill(null)
              .map((_, index) => campaignData.trainingReminders[index])
          : []
      const { managerNotificationBanner, managerReportEmail } = buildManagerNotificationSavePayload(
        state$.value
      )
      const triggerBasedCampaignData = campaignData.triggerBasedCampaignData
      const isTriggerBasedCampaign = !!triggerBasedCampaignData
      const timezone = isTriggerBasedCampaign
        ? selectActualTimezone(state$.value)
        : campaignData.timezone
      const SCHEDULE_FORMAT = 'YYYY-MM-DDTHH:mm'
      const scheduleTime = triggerBasedCampaignData
        ? moment(`${campaignData.launchDate} 09:00`).format(SCHEDULE_FORMAT)
        : moment(`${campaignData.launchDate} ${campaignData.launchTime}`).format(SCHEDULE_FORMAT)
      const closeTime = isTriggerBasedCampaign ? campaignData.endDate : campaignData.closeTime

      return api.wizardCampaign
        .saveCampaign(state$.value.partnerOptions.selectedCompany?.value, {
          campaignId: campaignData.id,
          name: campaignData.name,
          defaultLocaleId: campaignData.defaultLocaleId,
          selectedLocaleIds: campaignData.selectedLocaleIds,
          isTournament: campaignData.isTournament,
          participantsChoices: campaignData.participants.included,
          participantsExcludeChoices: campaignData.participants.excluded,
          timezone,
          scheduleTime,
          endDate: moment(campaignData.endDate).format('YYYY-MM-DD'),
          closeTime: moment(closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
          sendRemindersUntil: isTriggerBasedCampaign ? null : campaignData.sendRemindersUntil,
          emailLimit: isTriggerBasedCampaign ? undefined : campaignData.maxEmailPerDay,
          randomized: isTriggerBasedCampaign ? false : campaignData.randomized,
          sendAllWeek: campaignData.sendAllWeek,
          scenariosIds: campaignData.selectedScenarios.map((ss) => ss.id),
          landingPageId: campaignData.selectedLandingPage.id,
          trainingIds,
          randomizedScenarios: campaignData.randomizedScenarios,
          deliveryType: campaignData.delivery,
          sendReminderEveryXBusinessDays: campaignData.sendReminderEveryXBusinessDays,
          campaignTemplateId: campaignData.campaignTemplateId,
          localesByProfiles: campaignData.localesByProfiles,
          sendCampaignFailedEmail: campaignData.sendCampaignFailedEmail,
          sendCampaignSummaryToCampaignReportRecipients: isTriggerBasedCampaign
            ? false
            : campaignData.sendCampaignSummaryToCampaignReportRecipients,
          trainingReminders,
          notifications: campaignData.notifications,
          managerReportRemindersThreshold: campaignData.managerReportRemindersThreshold,
          sendManagerReportEveryXDays: campaignData.sendManagerReportEveryXDays,
          managerReportDayOfWeek: campaignData.managerReportDayOfWeek,
          managerNotificationBanner,
          managerReportEmail,
          maxRemindersPerParticipant: isTriggerBasedCampaign
            ? campaignData.maxRemindersPerParticipant
            : null,
          triggerBasedCampaignData: isTriggerBasedCampaign ? triggerBasedCampaignData : null,
          completionNotificationSettings: isTriggerBasedCampaign
            ? campaignData.completionNotificationSettings
            : null,
        })
        .pipe(
          mergeMap(({ response }) => of(mapSaveSuccessCallbacks[action.type](response, action))),
          catchError((error) => of(epicsErrorHandler(mapSaveErrorCallbacks[action.type], error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

const removeDraftEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REMOVE_DRAFT_ASYNC.INITIATE),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSimulationCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.awarenessCenter
        .deleteCampaign(state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.id)
        .pipe(
          mergeMap(({ response }) => of(removeDraftSuccess(response))),
          catchError((error) => of(epicsErrorHandler(removeDraftError, error))),
          takeUntil(action$.pipe(ofType(REMOVE_DRAFT_ASYNC.CANCEL)))
        )
    )
  )

const approveCampaignEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_AND_APPROVE_CAMPAIGN_ASYNC.SUCCESS),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSimulationCampaign) return true
      return false
    }),
    mergeMap(() =>
      api.wizardCampaign
        .approveCampaign(state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.id)
        .pipe(
          mergeMap(({ response }) => of(approveCampaignSuccess(response))),
          catchError((error) => of(epicsErrorHandler(approveCampaignError, error, [400]))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    )
  )

const sendTryItOutEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_AND_TEST_CAMPAIGN_ASYNC.SUCCESS),
    filter(() => {
      if (state$.value.awarenessCenter.wizardSimulationCampaign) return true
      return false
    }),
    mergeMap((action) => {
      const testCampaignData = selectTestCampaignData(state$.value)
      const sendCampaignFailedEmail = selectSendCampaignFailedEmail(state$.value)
      const sendManagerReportEveryXDays = selectSendManagerReportEveryXDays(state$.value)

      return api.wizardCampaign
        .postTryItOut({
          campaignId: state$.value.awarenessCenter.wizardSimulationCampaign.self.campaign.id,
          profileIds: testCampaignData.profileIds,
          scenarios: action.payload.initialAction.payload.scenarios,
          trainingReminderIndices: testCampaignData.includeTrainingReminders
            ? testCampaignData.trainingReminderIndices
                .filter((index) => typeof index === 'number')
                .map((index) => index + 1)
            : undefined,
          sendNotifyManagerEmail: sendManagerReportEveryXDays
            ? testCampaignData.sendNotifyManagerEmail
            : undefined,
          sendSimulationFailedEmail: sendCampaignFailedEmail
            ? testCampaignData.sendSimulationFailedEmail
            : undefined,
          sendSimulationFailureEmail: testCampaignData.sendSimulationFailureEmail,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              sendTryItOutSuccess(response),
              addToast({ message: 'Test campaign was sent successfully!' }),
              shareReset()
            )
          ),
          catchError((error) => of(epicsErrorHandler(sendTryItOutError, error))),
          takeUntil(action$.pipe(ofType(SAVE_DRAFT_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  combineEpics(
    saveDraftEpic,
    getCampaignEpic,
    removeDraftEpic,
    sendTryItOutEpic,
    approveCampaignEpic
  ),
  scenariosStepEpics,
  landingPageStepEpics
)
